import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { BaseComponentImpl, RoomImpl, LevelImpl } from '../../../types/implementations';
import { Building, PhotoLocation, PhotoModel, DoorCategory } from '../../../types/protocols';
import { translate, allDirectionsLabel } from '../../../types/EnumTranslations';
import { getMaterialTranslation } from '../../../contexts/MaterialsContext';
import { BaseType, CardinalDirection } from '../../../types/Enums';
import { ORDERED_CARDINAL_DIRECTIONS } from '../../../Helpers/componentSortUtils';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { RiskBadge } from '../../BuildingDetails/RisksOverview/RisksOverviewList';
import ClickableImage from '../../../Components/ClickableImage';
import PhotoThumbnailWithMarker from '../../../Components/PhotoThumbnailWithMarker';
import HoverPreview, { HoverPreviewImage } from '../../../Components/HoverPreview';
import { LocationData, LocationModal } from '../../../Components/LocationDisplay';
import { GroupingStrategy } from '../GenericComponentListPage';
import { getComponentRoomKey } from '../../../Helpers/ComponentRoomUtils';
import { groupByModularSize } from '../../../Helpers/modularSize';
import { allBlueprintImages } from '../../../Helpers/blueprintLookup';
import { MOUNTING_SURFACE_LABELS, MountingSurface, roofPlanParentRoofKey, utilityMountingSurface } from '../../../Helpers/roofPlan';
import { naturalCompare } from '../../../Helpers/naturalSort';
import { photoLocationList, primaryPhotoLocation } from '../../../Helpers/photoLocations';
import { satellitePhotoLocation } from '../../../Helpers/mapLocations';

const COMPASS_ICON_MAP: Record<CardinalDirection, string> = {
    [CardinalDirection.North]:     '/Images/Compass/Kompass-N.png',
    [CardinalDirection.NorthEast]: '/Images/Compass/Kompass-NO.png',
    [CardinalDirection.East]:      '/Images/Compass/Kompass-O.png',
    [CardinalDirection.SouthEast]: '/Images/Compass/Kompass-SO.png',
    [CardinalDirection.South]:     '/Images/Compass/Kompass-S.png',
    [CardinalDirection.SouthWest]: '/Images/Compass/Kompass-SV.png',
    [CardinalDirection.West]:      '/Images/Compass/Kompass-V.png',
    [CardinalDirection.NorthWest]: '/Images/Compass/Kompass-NV.png',
};

interface ListViewProps {
    components: BaseComponentImpl[];
    building: Building;
    buildingKey: string;
    selectedKey: string | null;
    onComponentClick: (key: string) => void;
    groupingStrategy: GroupingStrategy;
}

const getConditionColor = (condition: number): string => {
    if (condition >= 8) return '#28a745';
    if (condition >= 6) return '#ffc107';
    if (condition >= 4) return '#fd7e14';
    return '#dc3545';
};

const CATEGORY_HEADER_STYLE: React.CSSProperties = {
    marginBottom: '12px',
    fontWeight: 600,
};

const PHOTO_SIZE = 56;

/** A position thumbnail that shows an enlargement, marker included, on hover (molnfast-react#1859). */
const PositionThumbnail: React.FC<{ photoLocation: PhotoLocation; size: number; onClick?: () => void }> = ({ photoLocation, size, onClick }) => (
    <HoverPreview renderPreview={(previewSize) => (
        <HoverPreviewImage
            storagePath={photoLocation.storagePath}
            width={photoLocation.width}
            height={photoLocation.height}
            size={previewSize}
            marker={{ x: photoLocation.x, y: photoLocation.y }}
        />
    )}>
        <PhotoThumbnailWithMarker photoLocation={photoLocation} size={size} onClick={onClick} disableClick={!onClick} />
    </HoverPreview>
);
const MAX_PHOTOS = 2;
/** Satellite thumbnail in a group header: a few px shy of the header's height (title line + padding). */
const GROUP_MAP_SIZE = 36;

interface ChildCounts {
    windowCount: number;
    doorCount: number;
    sheetMetalCount: number;
    chimneyCount: number;
    drainageCount: number;
    balconyCount: number;
    miscellaneousCount: number;
}

/** Above this many components every group starts collapsed. */
const COLLAPSE_THRESHOLD = 30;

const EMPTY_CHILD_COUNTS: ChildCounts = {
    windowCount: 0, doorCount: 0, sheetMetalCount: 0, chimneyCount: 0, drainageCount: 0, balconyCount: 0, miscellaneousCount: 0,
};

interface ComponentRowProps {
    component: BaseComponentImpl;
    building: Building;
    buildingKey: string;
    isSelected: boolean;
    onClick: () => void;
    getTranslation: (key: string | undefined, fallbackBaseType?: string) => string;
    childCounts: ChildCounts;
    blueprintLookup: Map<string, PhotoModel>;
    labelSuffix?: string;
}

const ComponentRow: React.FC<ComponentRowProps> = ({
    component,
    building,
    buildingKey,
    isSelected,
    onClick,
    getTranslation,
    childCounts,
    blueprintLookup,
    labelSuffix,
}) => {
    const conditionValue = component.condition?.value?.value;
    const materials = component.material?.value;
    const area = component.areaShapes?.value ? component.totalArea(building) : null;
    const { windowCount, doorCount, sheetMetalCount, chimneyCount, drainageCount, balconyCount, miscellaneousCount } = childCounts;

    const hasHazards = !!component.hazardous?.value;
    const risks = component.buildingRisks?.value?.risks;
    const customRisk = component.buildingRisks?.value?.customRisk;

    const photos = component.photos ? Object.entries(component.photos) : [];

    // Photo location — the primary pin; the count of further pinned photos is shown next to it
    const photoLocation = primaryPhotoLocation(component, building);
    const extraPhotoLocations = photoLocation ? photoLocationList(component, building).length - 1 : 0;

    // Map location — the component's pin on the current satellite image
    const mapPhotoLocation = satellitePhotoLocation(component, building);

    // Blueprint location — first available, looked up via blueprintLookup
    const blueprintPhotoLocation: PhotoLocation | null = useMemo(() => {
        if (!component.blueprintLocations) return null;
        for (const [bpKey, loc] of Object.entries(component.blueprintLocations)) {
            const bp = blueprintLookup.get(bpKey);
            if (bp) {
                return {
                    parentKey: component.key,
                    storagePath: bp.storagePath,
                    width: loc.width,
                    height: loc.height,
                    x: loc.x,
                    y: loc.y,
                };
            }
        }
        return null;
    }, [component, blueprintLookup]);

    const lastUpdated = component.lastUpdated();

    return (
        <div
            onClick={onClick}
            className={`generic-component-list-item std-box dropshadow dropshadow-hover${isSelected ? ' selected' : ''}`}
        >
            <div className="gcl-content">
                <div className="gcl-header">
                    <span className="gcl-name">
                        {component.displayName(building)}{labelSuffix && <span className="gcl-name-suffix">{labelSuffix}</span>}
                    </span>
                    {conditionValue != null && (
                        <span
                            title={`Skick: ${conditionValue}/10`}
                            className="gcl-condition"
                            style={{ backgroundColor: getConditionColor(conditionValue) }}
                        />
                    )}
                    <span
                        className="gcl-updated"
                        title={`Senast uppdaterad ${lastUpdated} – registrering, ändrade uppgifter eller nya foton`}
                    >
                        <RefreshCw size={11} aria-hidden="true" />
                        {lastUpdated}
                    </span>
                </div>

                <div className="gcl-labels">
                    {component.specificType?.value && (
                        <span className="gcl-label">{getTranslation(component.specificType.value, component.type)}</span>
                    )}
                    {materials && materials.map((m, i) => (
                        <span key={i} className="gcl-label">{getMaterialTranslation(m)}</span>
                    ))}
                    {area != null && (
                        <span className="gcl-label">{area.toFixed(1)} kvm</span>
                    )}
                    {windowCount > 0 && <span className="gcl-label">{windowCount} fönster</span>}
                    {doorCount > 0 && <span className="gcl-label">{doorCount} {doorCount === 1 ? 'dörr' : 'dörrar'}</span>}
                    {balconyCount > 0 && <span className="gcl-label">{balconyCount} {balconyCount === 1 ? 'balkong' : 'balkonger'}</span>}
                    {sheetMetalCount > 0 && <span className="gcl-label">{sheetMetalCount} plåtarbeten</span>}
                    {chimneyCount > 0 && <span className="gcl-label">{chimneyCount} {translate(BaseType.Chimney).toLowerCase()}</span>}
                    {drainageCount > 0 && <span className="gcl-label">{drainageCount} {translate(BaseType.RoofDrainage).toLowerCase()}</span>}
                    {miscellaneousCount > 0 && <span className="gcl-label">{miscellaneousCount} {translate(BaseType.Miscellaneous).toLowerCase()}</span>}

                    {hasHazards && <RiskBadge type="hazard" />}
                    {(risks && risks.length > 0 || customRisk) && <RiskBadge type="riskConstruction" />}
                </div>
            </div>

            {(photoLocation || mapPhotoLocation || blueprintPhotoLocation || photos.length > 0) && (
                <div className="gcl-photos">
                    {photoLocation && (
                        <PositionThumbnail photoLocation={photoLocation} size={PHOTO_SIZE} />
                    )}
                    {extraPhotoLocations > 0 && (
                        <div className="gcl-photo-overflow" title={`Markerad i ${extraPhotoLocations + 1} foton`}>
                            +{extraPhotoLocations} foton
                        </div>
                    )}
                    {mapPhotoLocation && (
                        <PositionThumbnail photoLocation={mapPhotoLocation} size={PHOTO_SIZE} />
                    )}
                    {blueprintPhotoLocation && (
                        <PositionThumbnail photoLocation={blueprintPhotoLocation} size={PHOTO_SIZE} />
                    )}
                    {photos.slice(0, MAX_PHOTOS).map(([key, photo]) => (
                        <HoverPreview key={key} renderPreview={(previewSize) => (
                            <HoverPreviewImage storagePath={photo.storagePath} width={photo.width} height={photo.height} size={previewSize} />
                        )}>
                            <ClickableImage
                                buildingKey={buildingKey}
                                photo={photo}
                                disableClick={true}
                                size={PHOTO_SIZE}
                            />
                        </HoverPreview>
                    ))}
                    {photos.length > MAX_PHOTOS && (
                        <div className="gcl-photo-overflow">
                            +{photos.length - MAX_PHOTOS}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const ListView: React.FC<ListViewProps> = ({
    components,
    building,
    buildingKey,
    selectedKey,
    onComponentClick,
    groupingStrategy,
}) => {
    const { getTranslation, types } = useBuildComponentTypes();

    // Build child count index: single pass over all building components → O(1) per row
    const childCountIndex = useMemo(() => {
        const index = new Map<string, ChildCounts>();
        const allComponents = building.model?.structure?.components;
        if (!allComponents) return index;

        Object.values(allComponents).forEach(comp => {
            const addCount = (parentKey: string, field: keyof ChildCounts) => {
                if (!index.has(parentKey)) {
                    index.set(parentKey, { ...EMPTY_CHILD_COUNTS });
                }
                index.get(parentKey)![field]++;
            };

            if (comp.type === BaseType.Window || comp.type === BaseType.Door) {
                const field: keyof ChildCounts = comp.type === BaseType.Window ? 'windowCount' : 'doorCount';
                const surfaces = [
                    comp.parentSurface?.value,
                    comp.parentSurface1?.value,
                    comp.parentSurface2?.value,
                ].filter(Boolean) as string[];
                surfaces.forEach(key => addCount(key, field));
            } else if (comp.type === BaseType.SheetMetal) {
                const key = comp.parentSurface?.value;
                if (key) addCount(key, 'sheetMetalCount');
            } else if (comp.type === BaseType.Chimney) {
                const key = comp.parentSurface?.value;
                if (key) addCount(key, 'chimneyCount');
            } else if (comp.type === BaseType.RoofDrainage) {
                const key = comp.parentSurface?.value;
                if (key) addCount(key, 'drainageCount');
            } else if (comp.type === BaseType.Balcony && !comp.archivedAt) {
                // Archived balconies are not listed, so they are not counted either
                const key = comp.parentSurface?.value;
                if (key) addCount(key, 'balconyCount');
            } else if (comp.type === BaseType.Miscellaneous && !comp.archivedAt && (comp.parentRoomKeys?.value?.length ?? 0) === 0) {
                // Mounted on the surface; a room-bound Övrigt belongs to its rooms, not to a surface
                const key = comp.parentSurface?.value;
                if (key) addCount(key, 'miscellaneousCount');
            }
        });

        return index;
    }, [building]);

    // Build blueprint lookup: blueprintKey → PhotoModel (levels and the roof drawing, single pass)
    const blueprintLookup = useMemo(() => allBlueprintImages(building.model?.structure), [building]);

    // Long lists start collapsed so the groups fit on one screen; short ones stay open.
    const manyComponents = components.length > COLLAPSE_THRESHOLD;
    const [expandAllSignal, setExpandAllSignal] = useState({ expand: !manyComponents, count: 0 });
    const toggleAll = () => setExpandAllSignal(prev => ({ expand: !prev.expand, count: prev.count + 1 }));

    // ── Shared props for DirectionGroup ────────────────────────────
    const groupProps = {
        building,
        buildingKey,
        selectedKey,
        onComponentClick,
        getTranslation,
        childCountIndex,
        blueprintLookup,
        defaultExpanded: !manyComponents,
        expandAllSignal,
    };

    // ── Resolve parent surface display name ─────────────────────
    const resolveParentName = (parentKey: string): string | null => {
        const allComponents = building.model.structure?.components ?? {};
        const rooms = building.model.structure?.rooms ?? {};
        // Check if parent is a component (facade/roof)
        if (allComponents[parentKey]) {
            return new BaseComponentImpl(allComponents[parentKey]).displayName(building);
        }
        // Check if parent is a room
        if (rooms[parentKey]) {
            const room = new RoomImpl(rooms[parentKey]);
            return room.displayName();
        }
        return null;
    };

    // ── Build multi-parent label suffix for a component ─────────
    const getMultiParentSuffix = (component: BaseComponentImpl): string => {
        const ps0 = component.parentSurface?.value;
        const ps1 = component.parentSurface1?.value;
        if (!ps0 || !ps1) return '';
        const name0 = resolveParentName(ps0);
        const name1 = resolveParentName(ps1);
        if (name0 && name1) return ` (${name0} ↔ ${name1})`;
        return '';
    };

    // ── Grouping: parentSurface ─────────────────────────────────
    const renderParentSurfaceGrouping = () => {
        const allComponents = building.model.structure?.components ?? {};
        const groups = new Map<string, { parent: BaseComponentImpl; components: BaseComponentImpl[] }>();
        const ungrouped: BaseComponentImpl[] = [];

        components.forEach(component => {
            const parentKey = component.parentSurface?.value
                || component.parentSurface1?.value
                || component.parentSurface2?.value;
            if (parentKey && allComponents[parentKey]) {
                if (!groups.has(parentKey)) {
                    groups.set(parentKey, { parent: new BaseComponentImpl(allComponents[parentKey]), components: [] });
                }
                groups.get(parentKey)!.components.push(component);
            } else {
                ungrouped.push(component);
            }
        });

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {Array.from(groups.values()).map(({ parent, components: parentComponents }) => (
                    <DirectionGroup key={parent.key} title={parent.displayName(building)} parent={parent} components={parentComponents} {...groupProps} />
                ))}
                {(() => {
                    if (ungrouped.length === 0) return null;
                    const rooms = building.model.structure?.rooms ?? {};
                    const levels = building.model.structure?.levels ?? {};
                    const roomGroups = new Map<string, { title: string; components: BaseComponentImpl[] }>();
                    const unplaced: BaseComponentImpl[] = [];

                    ungrouped.forEach(component => {
                        const roomKey = getComponentRoomKey(component) || component.parentRoomKeys?.value?.[0];
                        if (roomKey && rooms[roomKey]) {
                            if (!roomGroups.has(roomKey)) {
                                const room = new RoomImpl(rooms[roomKey]);
                                let title = room.displayName();
                                if (room.levelKey && levels[room.levelKey]) {
                                    const level = new LevelImpl(levels[room.levelKey]);
                                    title = `${level.displayName()} – ${title}`;
                                }
                                roomGroups.set(roomKey, { title, components: [] });
                            }
                            roomGroups.get(roomKey)!.components.push(component);
                        } else {
                            unplaced.push(component);
                        }
                    });

                    return (
                        <>
                            {Array.from(roomGroups.values()).map(group => (
                                <DirectionGroup key={group.title} title={group.title} components={group.components} {...groupProps} />
                            ))}
                            {unplaced.length > 0 && (
                                <DirectionGroup title="Ej placerade" components={unplaced} {...groupProps} />
                            )}
                        </>
                    );
                })()}
            </div>
        );
    };

    // ── Grouping: room ──────────────────────────────────────────
    const renderRoomGrouping = () => {
        const rooms = building.model.structure?.rooms ?? {};
        const levels = building.model.structure?.levels ?? {};
        const allComponents = building.model.structure?.components ?? {};

        const levelGroups = new Map<string, {
            level: LevelImpl;
            roomGroups: Map<string, { room: RoomImpl; components: BaseComponentImpl[] }>;
        }>();
        const noLevelRooms = new Map<string, { room: RoomImpl; components: BaseComponentImpl[] }>();
        // Utilities mounted on a roof or facade (parentSurface, no rooms): "Tak" / "Fasad" groups
        const mounted: Record<MountingSurface, BaseComponentImpl[]> = { roof: [], facade: [] };
        const outside: BaseComponentImpl[] = [];
        const unplaced: BaseComponentImpl[] = [];

        components.forEach(component => {
            const roomKey = getComponentRoomKey(component) || component.parentRoomKeys?.value?.[0];
            const mounting = utilityMountingSurface(component, allComponents);
            if (mounting) {
                mounted[mounting].push(component);
            } else if (roomKey && rooms[roomKey]) {
                const room = new RoomImpl(rooms[roomKey]);
                if (room.levelKey && levels[room.levelKey]) {
                    if (!levelGroups.has(room.levelKey)) {
                        const level = new LevelImpl(levels[room.levelKey]);
                        levelGroups.set(room.levelKey, { level, roomGroups: new Map() });
                    }
                    const lg = levelGroups.get(room.levelKey)!;
                    if (!lg.roomGroups.has(roomKey)) {
                        lg.roomGroups.set(roomKey, { room, components: [] });
                    }
                    lg.roomGroups.get(roomKey)!.components.push(component);
                } else {
                    if (!noLevelRooms.has(roomKey)) {
                        noLevelRooms.set(roomKey, { room, components: [] });
                    }
                    noLevelRooms.get(roomKey)!.components.push(component);
                }
            } else if (component.mapLocations && Object.keys(component.mapLocations).length > 0) {
                outside.push(component);
            } else {
                unplaced.push(component);
            }
        });

        const sortedLevels = Array.from(levelGroups.values())
            .sort((a, b) => a.level.number - b.level.number);

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {sortedLevels.map(({ level, roomGroups }) => (
                    <div key={level.key}>
                        <h4 className="generic-component-section-header">
                            {level.displayName()}
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingLeft: '12px' }}>
                            {Array.from(roomGroups.values()).map(({ room, components: roomComponents }) => (
                                <DirectionGroup
                                    key={room.key || room.number || room.label || 'room'}
                                    title={room.displayName()}
                                    components={roomComponents}
                                    {...groupProps}
                                />
                            ))}
                        </div>
                    </div>
                ))}
                {Array.from(noLevelRooms.values()).map(({ room, components: roomComponents }) => (
                    <DirectionGroup
                        key={room.key || room.number || room.label || 'room'}
                        title={room.displayName()}
                        components={roomComponents}
                        {...groupProps}
                    />
                ))}
                {(['roof', 'facade'] as MountingSurface[]).map(mounting => mounted[mounting].length > 0 && (
                    <DirectionGroup key={`mounted-${mounting}`} title={MOUNTING_SURFACE_LABELS[mounting]} components={mounted[mounting]} {...groupProps} />
                ))}
                {outside.length > 0 && (
                    <DirectionGroup title="Utomhus" components={outside} {...groupProps} />
                )}
                {unplaced.length > 0 && (
                    <DirectionGroup
                        // Fast inredning is room-bound only: without a room it is "Utan rum"
                        title={unplaced.every(c => c.type === BaseType.FixedFurnishing) ? 'Utan rum' : 'Ej placerade'}
                        components={unplaced}
                        {...groupProps}
                    />
                )}
            </div>
        );
    };

    // ── Grouping: direction ─────────────────────────────────────
    const renderDirectionGroups = (list: BaseComponentImpl[]) => (
        <>
            {ORDERED_CARDINAL_DIRECTIONS.map((direction) => {
                const filtered = list.filter(
                    (c) => c.cardinalDirection?.value === direction
                );
                if (filtered.length === 0) return null;
                return (
                    <DirectionGroup key={direction} title={translate(direction)} compassIcon={COMPASS_ICON_MAP[direction]} components={filtered} {...groupProps} />
                );
            })}
            {(() => {
                const withoutDirection = list.filter(
                    (c) => c.cardinalDirection?.value === undefined
                );
                if (withoutDirection.length === 0) return null;
                return (
                    <DirectionGroup title={allDirectionsLabel(withoutDirection[0].type)} compassIcon="/Images/Compass/Kompass-Undef.png" components={withoutDirection} {...groupProps} />
                );
            })()}
        </>
    );

    const renderDirectionGrouping = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {renderDirectionGroups(components)}
        </div>
    );

    // ── Grouping: roofPlan ──────────────────────────────────────
    // The takplan (D6): roofs by direction as before, then what sits on each
    // roof (chimneys, drainage, sheet metal, roof safety, mounted utilities,
    // takfönster) under the roof's name, one group per base type.
    const renderRoofPlanGrouping = () => {
        const roofs = components.filter(c => c.type === BaseType.Roof);
        const roofKeys = new Set(roofs.map(roof => roof.key));
        const mountedByRoof = new Map<string, Map<string, BaseComponentImpl[]>>();
        const withoutRoof: BaseComponentImpl[] = [];

        components.forEach(component => {
            if (component.type === BaseType.Roof) return;
            const roofKey = roofPlanParentRoofKey(component, roofKeys);
            if (!roofKey) {
                withoutRoof.push(component);
                return;
            }
            const byType = mountedByRoof.get(roofKey) ?? new Map<string, BaseComponentImpl[]>();
            byType.set(component.type, [...(byType.get(component.type) ?? []), component]);
            mountedByRoof.set(roofKey, byType);
        });

        // Roofs in the order the direction groups show them, then by name
        const directionIndex = (roof: BaseComponentImpl) => {
            const direction = roof.cardinalDirection?.value;
            const index = direction ? ORDERED_CARDINAL_DIRECTIONS.indexOf(direction) : -1;
            return index === -1 ? ORDERED_CARDINAL_DIRECTIONS.length : index;
        };
        const byDisplayName = (a: BaseComponentImpl, b: BaseComponentImpl) =>
            naturalCompare(a.displayName(building), b.displayName(building)) || a.key.localeCompare(b.key);
        const roofSections = roofs
            .filter(roof => mountedByRoof.has(roof.key))
            .sort((a, b) => directionIndex(a) - directionIndex(b) || byDisplayName(a, b))
            .map(roof => ({
                roof,
                groups: Array.from(mountedByRoof.get(roof.key)!.entries())
                    .map(([type, typeComponents]) => ({
                        type,
                        title: translate(type),
                        components: [...typeComponents].sort(byDisplayName),
                    }))
                    .sort((a, b) => a.title.localeCompare(b.title, 'sv')),
            }));

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {renderDirectionGroups(roofs)}
                {roofSections.map(({ roof, groups }) => (
                    <div key={roof.key}>
                        <h4 className="generic-component-section-header">
                            {roof.displayName(building)}
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingLeft: '12px' }}>
                            {groups.map(group => (
                                <DirectionGroup key={group.type} title={group.title} components={group.components} {...groupProps} />
                            ))}
                        </div>
                    </div>
                ))}
                {withoutRoof.length > 0 && (
                    <DirectionGroup title="Ej placerade" components={withoutRoof} {...groupProps} />
                )}
            </div>
        );
    };

    // ── Grouping: doorCategory ──────────────────────────────────
    const renderDoorCategoryGrouping = () => {
        const exterior: BaseComponentImpl[] = [];
        const interior: BaseComponentImpl[] = [];
        const uncategorized: BaseComponentImpl[] = [];

        components.forEach(component => {
            const specificTypeValue = component.specificType?.value;
            const category = specificTypeValue ? types[specificTypeValue]?.doorProps?.doorCategory : undefined;
            if (category === DoorCategory.exterior_door) exterior.push(component);
            else if (category === DoorCategory.interior_door) interior.push(component);
            else uncategorized.push(component);
        });

        const allComponents = building.model.structure?.components ?? {};
        const rooms = building.model.structure?.rooms ?? {};
        const levels = building.model.structure?.levels ?? {};

        // Sub-group exterior doors by parent facade
        const renderExteriorSection = () => {
            if (exterior.length === 0) return null;
            const facadeGroups = new Map<string, { parent: BaseComponentImpl; components: BaseComponentImpl[] }>();
            const unplaced: BaseComponentImpl[] = [];

            exterior.forEach(component => {
                const parentKey = component.parentSurface?.value
                    || component.parentSurface1?.value
                    || component.parentSurface2?.value;
                if (parentKey && allComponents[parentKey]) {
                    if (!facadeGroups.has(parentKey)) {
                        facadeGroups.set(parentKey, { parent: new BaseComponentImpl(allComponents[parentKey]), components: [] });
                    }
                    facadeGroups.get(parentKey)!.components.push(component);
                } else {
                    unplaced.push(component);
                }
            });

            return (
                <div>
                    <h3 style={CATEGORY_HEADER_STYLE}>{`Ytterdörrar (${exterior.length})`}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingLeft: '12px' }}>
                        {Array.from(facadeGroups.values()).map(({ parent, components: facadeComponents }) => (
                            <DirectionGroup key={parent.key} title={parent.displayName(building)} parent={parent} components={facadeComponents} {...groupProps} labelSuffix={getMultiParentSuffix} />
                        ))}
                        {unplaced.length > 0 && (
                            <DirectionGroup title="Ej placerade" components={unplaced} {...groupProps} labelSuffix={getMultiParentSuffix} />
                        )}
                    </div>
                </div>
            );
        };

        // Sub-group interior doors by room
        const renderInteriorSection = () => {
            if (interior.length === 0) return null;
            const levelGroups = new Map<string, {
                level: LevelImpl;
                roomGroups: Map<string, { room: RoomImpl; components: BaseComponentImpl[] }>;
            }>();
            const noLevelRooms = new Map<string, { room: RoomImpl; components: BaseComponentImpl[] }>();
            const unplaced: BaseComponentImpl[] = [];

            interior.forEach(component => {
                const roomKey = getComponentRoomKey(component) || component.parentRoomKeys?.value?.[0];
                if (roomKey && rooms[roomKey]) {
                    const room = new RoomImpl(rooms[roomKey]);
                    if (room.levelKey && levels[room.levelKey]) {
                        if (!levelGroups.has(room.levelKey)) {
                            const level = new LevelImpl(levels[room.levelKey]);
                            levelGroups.set(room.levelKey, { level, roomGroups: new Map() });
                        }
                        const lg = levelGroups.get(room.levelKey)!;
                        if (!lg.roomGroups.has(roomKey)) {
                            lg.roomGroups.set(roomKey, { room, components: [] });
                        }
                        lg.roomGroups.get(roomKey)!.components.push(component);
                    } else {
                        if (!noLevelRooms.has(roomKey)) {
                            noLevelRooms.set(roomKey, { room, components: [] });
                        }
                        noLevelRooms.get(roomKey)!.components.push(component);
                    }
                } else {
                    // Interior doors without room go to "Ej placerade" (not "Utomhus")
                    unplaced.push(component);
                }
            });

            const sortedLevels = Array.from(levelGroups.values())
                .sort((a, b) => a.level.number - b.level.number);

            return (
                <div>
                    <h3 style={CATEGORY_HEADER_STYLE}>{`Innerdörrar (${interior.length})`}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingLeft: '12px' }}>
                        {sortedLevels.map(({ level, roomGroups }) => (
                            <div key={level.key}>
                                <h4 className="generic-component-section-header">
                                    {level.displayName()}
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingLeft: '12px' }}>
                                    {Array.from(roomGroups.values()).map(({ room, components: roomComponents }) => (
                                        <DirectionGroup
                                            key={room.key || room.number || room.label || 'room'}
                                            title={room.displayName()}
                                            components={roomComponents}
                                            {...groupProps}
                                            labelSuffix={getMultiParentSuffix}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                        {Array.from(noLevelRooms.values()).map(({ room, components: roomComponents }) => (
                            <DirectionGroup
                                key={room.key || room.number || room.label || 'room'}
                                title={room.displayName()}
                                components={roomComponents}
                                {...groupProps}
                                labelSuffix={getMultiParentSuffix}
                            />
                        ))}
                        {unplaced.length > 0 && (
                            <DirectionGroup title="Ej placerade" components={unplaced} {...groupProps} labelSuffix={getMultiParentSuffix} />
                        )}
                    </div>
                </div>
            );
        };

        // Uncategorized doors: flat list
        const renderUncategorizedSection = () => {
            if (uncategorized.length === 0) return null;
            return (
                <div>
                    <h3 style={CATEGORY_HEADER_STYLE}>{`Okategoriserade (${uncategorized.length})`}</h3>
                    <div className="component-list-container" style={{ paddingLeft: '12px' }}>
                        {uncategorized.map(component => (
                            <ComponentRow
                                key={component.key}
                                component={component}
                                building={building}
                                buildingKey={buildingKey}
                                isSelected={selectedKey === component.key}
                                onClick={() => onComponentClick(component.key)}
                                getTranslation={getTranslation}
                                childCounts={childCountIndex.get(component.key) ?? EMPTY_CHILD_COUNTS}
                                blueprintLookup={blueprintLookup}
                            />
                        ))}
                    </div>
                </div>
            );
        };

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {renderExteriorSection()}
                {renderInteriorSection()}
                {renderUncategorizedSection()}
            </div>
        );
    };

    // ── Grouping: dimensions (modulmått) ────────────────────────
    // Windows grouped by modular size in collapsible sections, smallest first.
    const renderDimensionGrouping = () => {
        const { groups, unsized } = groupByModularSize(components);
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {groups.map(group => (
                    <DirectionGroup
                        key={group.key}
                        title={group.title}
                        components={group.items}
                        {...groupProps}
                        defaultExpanded={false}
                    />
                ))}
                {unsized.length > 0 && (
                    <DirectionGroup title="Utan modulmått" components={unsized} {...groupProps} defaultExpanded={false} />
                )}
            </div>
        );
    };

    // ── Flat list (no grouping) ─────────────────────────────────
    const renderFlatList = () => (
        <div className="component-list-container">
            {components.map((component) => (
                <ComponentRow
                    key={component.key}
                    component={component}
                    building={building}
                    buildingKey={buildingKey}
                    isSelected={selectedKey === component.key}
                    onClick={() => onComponentClick(component.key)}
                    getTranslation={getTranslation}
                    childCounts={childCountIndex.get(component.key) ?? EMPTY_CHILD_COUNTS}
                    blueprintLookup={blueprintLookup}
                />
            ))}
        </div>
    );

    // ── Switch on grouping strategy ─────────────────────────────
    const renderGrouping = () => {
        switch (groupingStrategy) {
            case 'doorCategory':
                return renderDoorCategoryGrouping();
            case 'parentSurface':
                return renderParentSurfaceGrouping();
            case 'room':
                return renderRoomGrouping();
            case 'direction':
                return renderDirectionGrouping();
            case 'roofPlan':
                return renderRoofPlanGrouping();
            case 'dimensions':
                return renderDimensionGrouping();
            case 'none':
            default:
                return renderFlatList();
        }
    };

    if (groupingStrategy === 'none' || components.length === 0) {
        return renderGrouping();
    }

    return (
        <>
            <div className="generic-component-group-toolbar">
                <button type="button" className="generic-component-expand-all" onClick={toggleAll}>
                    {expandAllSignal.expand ? 'Fäll ihop alla' : 'Expandera alla'}
                </button>
            </div>
            {renderGrouping()}
        </>
    );
};

interface DirectionGroupProps {
    title: string;
    compassIcon?: string;
    /** The surface the group's components sit on (facade/roof); its satellite pin is shown in the header. */
    parent?: BaseComponentImpl;
    components: BaseComponentImpl[];
    building: Building;
    buildingKey: string;
    selectedKey: string | null;
    onComponentClick: (key: string) => void;
    getTranslation: (key: string | undefined, fallbackBaseType?: string) => string;
    childCountIndex: Map<string, ChildCounts>;
    blueprintLookup: Map<string, PhotoModel>;
    labelSuffix?: (component: BaseComponentImpl) => string;
    /** Start open; false for long lists, where all groups start collapsed. */
    defaultExpanded?: boolean;
    /** Bumped by the toolbar's expand/collapse-all control. */
    expandAllSignal?: { expand: boolean; count: number };
}

/**
 * A group of components with a chevron header. Collapsed groups keep long lists
 * (facades with 80 windows) scannable. The group holding the selected component
 * opens on its own so a click on a pin never hides the row.
 */
const DirectionGroup: React.FC<DirectionGroupProps> = ({
    title,
    compassIcon,
    parent,
    components,
    building,
    buildingKey,
    selectedKey,
    onComponentClick,
    getTranslation,
    childCountIndex,
    blueprintLookup,
    labelSuffix,
    defaultExpanded = true,
    expandAllSignal,
}) => {
    const holdsSelection = selectedKey != null && components.some(c => c.key === selectedKey);
    const [isExpanded, setIsExpanded] = useState(() => defaultExpanded || holdsSelection);
    const toggle = () => setIsExpanded(prev => !prev);

    // "Expandera alla" / "Fäll ihop alla" in the toolbar bumps the signal's counter.
    const signalCount = expandAllSignal?.count;
    const signalExpand = expandAllSignal?.expand;
    useEffect(() => {
        if (signalCount) setIsExpanded(!!signalExpand);
    }, [signalCount, signalExpand]);

    // Never leave the selected component hidden.
    useEffect(() => {
        if (holdsSelection) setIsExpanded(true);
    }, [holdsSelection]);

    // The parent surface's satellite pin, in the same place as the rows' thumbnails.
    // A click on it opens the satellite image full-screen instead of toggling the group.
    const parentMapLocation = useMemo(
        () => (parent ? satellitePhotoLocation(parent, building) : null),
        [parent, building],
    );
    const [showParentMap, setShowParentMap] = useState(false);
    const parentMapModalLocation: LocationData | null = useMemo(() => parentMapLocation && {
        type: 'map',
        label: `${title} – position på satellitbild`,
        storagePath: parentMapLocation.storagePath,
        x: parentMapLocation.x,
        y: parentMapLocation.y,
        width: parentMapLocation.width,
        height: parentMapLocation.height,
    }, [parentMapLocation, title]);

    return (
        <div className="generic-component-collapsible-group">
            <div
                className="generic-component-collapsible-header"
                onClick={toggle}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggle();
                    }
                }}
            >
                <div className="generic-component-collapsible-title">
                    {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    {compassIcon && (
                        <img
                            src={compassIcon}
                            alt=""
                            className="generic-component-section-compass"
                        />
                    )}
                    <span>{title}</span>
                    <span className="generic-component-collapsible-count">({components.length} st)</span>
                </div>
                {parentMapLocation && (
                    <div
                        className="generic-component-collapsible-map"
                        title="Visa position på satellitbilden"
                        // Swallow clicks on the loading/missing placeholders too, so they never toggle the group
                        onClick={(e) => e.stopPropagation()}
                    >
                        <PositionThumbnail
                            photoLocation={parentMapLocation}
                            size={GROUP_MAP_SIZE}
                            onClick={() => setShowParentMap(true)}
                        />
                    </div>
                )}
            </div>
            {showParentMap && parentMapModalLocation && (
                <LocationModal location={parentMapModalLocation} onClose={() => setShowParentMap(false)} />
            )}
            {isExpanded && (
                <div className="component-list-container generic-component-collapsible-body">
                    {components.map((component) => (
                        <ComponentRow
                            key={component.key}
                            component={component}
                            building={building}
                            buildingKey={buildingKey}
                            isSelected={selectedKey === component.key}
                            onClick={() => onComponentClick(component.key)}
                            getTranslation={getTranslation}
                            childCounts={childCountIndex.get(component.key) ?? EMPTY_CHILD_COUNTS}
                            blueprintLookup={blueprintLookup}
                            labelSuffix={labelSuffix?.(component)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ListView;
